/** 위키 (PRD 7장): 입력 규칙, 응답 형태, 링크 해석, 줄 단위 비교 */
import { diffLines } from "diff";
import { z } from "zod";
import { type DateStr, WEEKDAY_NAMES, weekday } from "./dates";

/** 채널에 속하지 않고 모두에게 공통인 위키. 링크는 [[공통/제목]] (예전 이름 [[전사/제목]]도 그대로 된다) */
export const WIKI_COMPANY_NAME = "공통";
const WIKI_COMPANY_ALIASES = [WIKI_COMPANY_NAME, "전사"];
/** 공동 편집 문서 이름: wiki:<페이지 id> */
export const wikiDocName = (pageId: number) => `wiki:${pageId}`;
/** 마지막 편집 후 이만큼 멈추면 자동 커밋 */
export const WIKI_IDLE_COMMIT_MS = 5 * 60 * 1000;

export const wikiTitleSchema = z
  .string()
  .trim()
  .min(1, "제목을 입력하세요.")
  .max(120, "제목은 120자까지입니다.")
  .refine((t) => !/[[\]|\n]/.test(t), "제목에는 [ ] | 를 쓸 수 없습니다. [[링크]] 문법에 쓰이기 때문입니다.");

const id = z.number().int().positive();

export const createWikiPageInput = z.object({
  title: wikiTitleSchema,
  parentId: id.nullable().optional(),
  markdown: z.string().max(500_000).optional(),
});

export const renameWikiPageInput = z.object({ title: wikiTitleSchema });

export const moveWikiPageInput = z.object({
  parentId: id.nullable(),
  /** 같은 부모 안에서의 순서 */
  position: z.number().finite(),
  /** 다른 공간으로 옮길 때 */
  spaceId: id.optional(),
});

export const publishWikiPageInput = z.object({ announce: z.boolean().default(false) });
export const commitWikiPageInput = z.object({ message: z.string().trim().max(200, "변경 요약은 200자까지입니다.").optional() });
export const restoreWikiInput = z.object({
  /** 함께 삭제된 하위 페이지도 되살릴지 (삭제 되돌리기) */
  withChildren: z.boolean().optional(),
});
/** 스레드를 위키 초안으로 정리 */
export const threadToWikiInput = z.object({ title: wikiTitleSchema });

export const purgeSearchInput = z.object({ q: z.string().min(2, "두 글자 이상 입력하세요.").max(200) });
export const purgeWikiInput = z.object({
  commitIds: z.array(id).min(1, "제거할 커밋을 고르세요.").max(500),
  reason: z.string().trim().min(1, "사유를 적으세요.").max(200),
});

export type WikiStatus = "draft" | "published";
export type WikiCommitKind = "create" | "edit" | "publish" | "rename" | "move" | "delete" | "restore" | "link_update" | "lock" | "unlock";
export const WIKI_COMMIT_KIND_NAMES: Record<WikiCommitKind, string> = {
  create: "생성",
  edit: "편집",
  publish: "게시",
  rename: "이름 변경",
  move: "이동",
  delete: "삭제",
  restore: "복원",
  link_update: "링크 갱신",
  lock: "잠금",
  unlock: "잠금 해제",
};

/** 페이지 잠금 (관리자만) */
export const lockWikiPageInput = z.object({ locked: z.boolean() });

/* ───────────── 응답 ───────────── */

export interface WikiSpaceDTO {
  id: number;
  /** 채널 위키면 채널, 공통 위키면 null */
  channelId: number | null;
  name: string;
}

/** 트리에 보이는 페이지 */
export interface WikiPageSummaryDTO {
  id: number;
  spaceId: number;
  parentId: number | null;
  position: number;
  title: string;
  status: WikiStatus;
  deleted: boolean;
  /** 관리자만 고칠 수 있게 잠김 */
  locked: boolean;
  createdById: number | null;
  updatedAt: string;
}

export interface WikiPageDTO extends WikiPageSummaryDTO {
  /** 마지막으로 저장된 내용 (편집 중이면 공동 편집 문서가 더 최신) */
  markdown: string;
  headCommitId: number | null;
  lastCommit: { id: number; seq: number; createdAt: string; editorIds: number[] } | null;
  /** 아직 커밋되지 않은 변경이 있는지 */
  dirty: boolean;
  /** 지금 이 페이지를 연 사람 */
  editingUserIds: number[];
  canEdit: boolean;
  watching: boolean;
  ancestors: { id: number; title: string }[];
}

export interface WikiCommitDTO {
  id: number;
  pageId: number;
  seq: number;
  kind: WikiCommitKind;
  title: string;
  spaceId: number;
  parentPageId: number | null;
  status: WikiStatus;
  deleted: boolean;
  message: string | null;
  autoMessage: string;
  editorIds: number[];
  actorId: number | null;
  restoredFromCommitId: number | null;
  groupId: string | null;
  isPrivate: boolean;
  createdAt: string;
  /** 영구 제거된 내용이면 */
  purged: { at: string; byId: number | null; reason: string | null } | null;
}

export interface WikiCommitViewDTO {
  commit: WikiCommitDTO;
  /** 영구 제거됐으면 null */
  markdown: string | null;
}

export interface WikiLogEntryDTO {
  type: "commit" | "purge";
  commit?: WikiCommitDTO & { pageTitle: string };
  purge?: { id: number; actorId: number | null; reason: string; count: number; createdAt: string };
  at: string;
}

export interface WikiDiffLine {
  op: "add" | "del" | "same";
  text: string;
}

export interface WikiBacklinkDTO {
  id: number;
  spaceId: number;
  title: string;
}

export interface WikiTitleDTO {
  id: number;
  spaceId: number;
  title: string;
  status: WikiStatus;
}

export interface WikiPurgeHitDTO {
  commitId: number;
  pageId: number;
  pageTitle: string;
  seq: number;
  kind: WikiCommitKind;
  spaceId: number;
  isPrivate: boolean;
  createdAt: string;
  /** 앞뒤 문맥 */
  snippet: string;
}

/* ───────────── 링크 해석 ───────────── */

export type WikiTargetSpace = { kind: "same" } | { kind: "company" } | { kind: "channel"; name: string };

/**
 * `[[제목]]`(같은 공간), `[[#채널/제목]]`, `[[공통/제목]]`(예전 `[[전사/제목]]`)의 대상.
 * 앞부분이 공간 표시가 아니면 "/"도 제목의 일부로 본다 ("2026/10 회의").
 */
export function splitWikiTarget(target: string): { space: WikiTargetSpace; title: string } {
  const t = target.trim();
  const channel = /^#([^/]+)\/(.+)$/.exec(t);
  if (channel) return { space: { kind: "channel", name: channel[1]!.trim().toLowerCase() }, title: channel[2]!.trim() };
  const company = new RegExp(`^(?:${WIKI_COMPANY_ALIASES.join("|")})/(.+)$`).exec(t);
  if (company) return { space: { kind: "company" }, title: company[1]!.trim() };
  return { space: { kind: "same" }, title: t };
}

/* ───────────── 줄 단위 비교 ───────────── */

/** 마지막 줄에 줄바꿈이 없으면 붙인다 (끝에 줄을 덧붙였을 때 앞줄이 바뀐 것으로 보이지 않게) */
const withEol = (s: string) => (s === "" || s.endsWith("\n") ? s : `${s}\n`);

export function lineDiff(before: string, after: string): WikiDiffLine[] {
  const out: WikiDiffLine[] = [];
  for (const part of diffLines(withEol(before), withEol(after))) {
    const op = part.added ? "add" : part.removed ? "del" : "same";
    const lines = part.value.replace(/\n$/, "").split("\n");
    for (const text of lines) out.push({ op, text });
  }
  return out;
}

/** 자동 요약: "3줄 추가, 1줄 삭제" */
export function diffSummary(before: string, after: string): string {
  let added = 0;
  let removed = 0;
  for (const part of diffLines(withEol(before), withEol(after))) {
    if (part.added) added += part.count;
    if (part.removed) removed += part.count;
  }
  if (!added && !removed) return "변경 없음";
  return [added && `${added}줄 추가`, removed && `${removed}줄 삭제`].filter(Boolean).join(", ");
}

/* ───────────── 템플릿 (PRD 7.8) ───────────── */

export const templateNameSchema = z.string().trim().min(1, "템플릿 이름을 입력하세요.").max(40, "템플릿 이름은 40자까지입니다.");
export const wikiTemplateInput = z.object({
  name: templateNameSchema,
  description: z.string().trim().max(200, "설명은 200자까지입니다.").default(""),
  /** 새 페이지 제목 (비우면 직접 쓴다). {{날짜}} 등을 쓸 수 있다 */
  title: z
    .string()
    .trim()
    .max(120, "제목은 120자까지입니다.")
    .refine((t) => !/[[\]|\n]/.test(t), "제목에는 [ ] | 를 쓸 수 없습니다.")
    .default(""),
  markdown: z.string().max(100_000, "템플릿이 너무 깁니다."),
});

export interface WikiTemplateDTO {
  /** 기본 템플릿은 "builtin:이름", 관리자가 만든 템플릿은 "custom:id" */
  key: string;
  /** 관리자가 만든 템플릿만 */
  id: number | null;
  name: string;
  description: string;
  title: string;
  markdown: string;
  builtin: boolean;
}

/** 기본 템플릿: 고칠 수 없고, 관리자는 템플릿을 더 만들 수 있다 */
export const BUILTIN_WIKI_TEMPLATES: WikiTemplateDTO[] = [
  {
    key: "builtin:meeting",
    id: null,
    name: "회의록",
    description: "날짜·참석자·논의·결정·액션 아이템",
    title: "{{날짜}} 회의",
    markdown: `**일시** {{날짜}} ({{요일}})
**참석자** {{작성자}}

## 논의

- 

## 결정

- 

## 액션 아이템

- [ ] 
`,
    builtin: true,
  },
  {
    key: "builtin:project",
    id: null,
    name: "프로젝트 개요",
    description: "배경·목표·범위·일정·할일",
    title: "",
    markdown: `## 배경

## 목표

- 

## 범위

- 하는 것: 
- 하지 않는 것: 

## 일정

| 단계 | 날짜 | 담당 |
| --- | --- | --- |
|  |  |  |

## 할일

- [ ] 

## 참고 자료

`,
    builtin: true,
  },
  {
    key: "builtin:checklist",
    id: null,
    name: "체크리스트",
    description: "체크 칸 목록. 줄마다 할일로 만들 수 있습니다",
    title: "",
    markdown: `- [ ] 
- [ ] 
- [ ] 
`,
    builtin: true,
  },
];

/** {{날짜}} → 2026-10-18, {{요일}} → 일, {{작성자}} → @아이디 (아이디가 없으면 이름) */
export function expandTemplate(text: string, vars: { date: DateStr; username: string | null; displayName: string }): string {
  return text
    .replaceAll("{{날짜}}", vars.date)
    .replaceAll("{{요일}}", WEEKDAY_NAMES[weekday(vars.date)]!)
    .replaceAll("{{작성자}}", vars.username ? `@${vars.username}` : vars.displayName);
}
